import styled from '@emotion/styled';
import { IMatchForAgent } from 'domain/entities/Match/Match';
import { CHAT_TYPE } from 'domain/entities/ChatRoom/ChatRoom';
import { industryImageMapping, industryMapping, getKeyByValue } from 'domain/entities/Company/Company';
import { SurveyButton } from 'ui/components/elements/Buttons/SurveyButton';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import NoImage from 'ui/images/no_image_case.png';
import { mqUser } from 'utils/responsive';
import { useNavigate } from 'react-router-dom';

interface Props {
    match: IMatchForAgent;
}

const Wrapper = styled.div`
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    background: var(--white);
    padding: 20px;
    margin: 0 auto;
    position: relative;

    ${mqUser('SP')} {
        padding: 16px 12px;
    }
`;

const ImageWrapper = styled.div`
    width: 200px;
    height: 140px;
    border-radius: 10px;
    background-color: #808080;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    :hover {
        opacity: 0.8;
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    ${mqUser('SP')} {
        width: 100%;
        height: auto;
        max-height: 200px;
        aspect-ratio: 4 / 3;
    }
`;

const Flex = styled.div`
    display: flex;
    gap: 20px;

    ${mqUser('SP')} {
        flex-direction: column;
        gap: 12px;
    }
`;

const FlexCenter = styled.div`
    display: flex;
    gap: 13px;
    align-items: center;
`;

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Title = styled.div`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 1px;
    :hover {
        opacity: 0.8;
    }

    img {
        width: 16px;
        height: 16px;
    }
`;

const Detail = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
`;

const Label = styled.div<{ color: string }>`
    color: var(--white);
    background-color: ${(props) => props.color};
    padding: 5px 10px;
    border-radius: 5px;
    font-size: var(--font-small);
    font-weight: 700;
    width: fit-content;
    height: fit-content;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 20px;
    justify-content: flex-end;
    position: absolute;
    right: 50px;
    bottom: 20px;

    ${mqUser('SP')} {
        position: static;
        justify-content: flex-start;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        margin-top: 20px;
    }
`;

const Button = styled.button`
    background-color: var(--main-color-blue);
    color: var(--white);
    border: none;
    font-weight: 700;
    font-size: var(--font-large);
    border-radius: 5px;
    padding: 8px 10px;
    cursor: pointer;

    :hover {
        opacity: 0.8;
    }

    ${mqUser('SP')} {
        width: 100%;
        font-size: var(--font-medium);
    }
`;

const DateContainer = styled.div`
    display: flex;
    gap: 10px;
`;

const Date = styled.div`
    background-color: #f3f3f3;
    font-size: var(--font-default);
    border-radius: 5px;
    padding: 3px 10px;
`;

const getIndustryImage = (industry: string | null) => {
    if (!industry) {
        return NoImage;
    }
    const industryKey = getKeyByValue(industryMapping, industry);
    const industryImage = industryImageMapping[industryKey as keyof typeof industryImageMapping];
    return industryImage;
};

const isCompany = (match: IMatchForAgent) => {
    return !!match.company.id;
};

const getCompanyImg = (match: IMatchForAgent) => {
    if (isCompany(match)) {
        return match.company.icon_image || getIndustryImage(match.company.industry);
    } else {
        return match.buyer.icon_image || NoImage;
    }
};

export const MatchCard: React.FC<Props> = ({ match }) => {
    const navigate = useNavigate();
    if (match.chat_room && match.chat_room?.chat_type === CHAT_TYPE.CHAT_TYPE_SUPPORT) {
        // サポートチャットは表示しない
        return null;
    }

    const companyObj = isCompany(match) ? match.company : match.buyer;
    const today = dayjs();

    return (
        <Wrapper>
            <Flex>
                {/* 画像が未登録の場合は業種画像 */}
                <ImageWrapper>
                    <img src={getCompanyImg(match)} alt="企業画像" />
                </ImageWrapper>
                <FlexColumn>
                    <FlexCenter>
                        <Title>{companyObj.name}</Title>
                        <Label color={isCompany(match) ? 'var(--main-color-blue)' : 'var(--primary)'}>
                            {isCompany(match) ? '売り手企業' : '買い手企業'}
                        </Label>
                    </FlexCenter>
                    {isCompany(match) && <Detail>業種：{match.company?.industry || '業種未設定'}</Detail>}
                    <DateContainer>
                        <Date>
                            面談日：
                            {match.meeting_date ? dayjs(match.meeting_date).format('YYYY/MM/DD') : '未設定'}
                        </Date>
                        <Date>
                            マッチング日：
                            {match.created_at ? dayjs(match.created_at).format('YYYY/MM/DD') : '未設定'}
                        </Date>
                    </DateContainer>

                    <Link
                        style={{ color: 'var(--main-color-blue)', fontWeight: 'bold' }}
                        to={
                            isCompany(match)
                                ? `/agent/companies/after_match/${match.company.id}/`
                                : `/agent/buyers/after_match/${match.buyer.id}/`
                        }
                        target="_blank"
                    >
                        企業情報を見る
                    </Link>
                </FlexColumn>
                <ButtonContainer>
                    {!match?.already_survey_answered && match.meeting_date && today.isAfter(match.meeting_date) && (
                        <SurveyButton disabled={false} onClick={() => navigate(`/agent/survey/form/${match.id}/`)} />
                    )}
                    <Link to={`/agent/message/${match.chat_room?.id}/`}>
                        <Button>チャットへ</Button>
                    </Link>
                </ButtonContainer>
            </Flex>
        </Wrapper>
    );
};
